import { Link, createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { discoverySource } from "../../sources/discoverySource";
import { itemLink } from "../../lib/itemLink";
import { useAmbientBackdrop } from "../../lib/pageBackdrop";
import { HeroBanner, MetaLine } from "../../components/hero-banner";
import { metaFacts } from "../../components/library/media-meta";
import { HubRow } from "../../components/media";
import { LoadingState } from "../../components/page-state";

export const Route = createFileRoute("/_authed/discover/")({
  component: Discover,
});

function Discover() {
  // An art route: the shell's backdrop carries the last art seen while
  // nothing of ours has decoded (lib/pageBackdrop) — first hook, before any
  // early return, so the spinner phase has it too.
  useAmbientBackdrop();
  const hubs = useQuery({
    queryKey: ["discover-hubs"],
    queryFn: () => discoverySource.getHomeHubs(),
    staleTime: 60 * 60 * 1000,
  });

  if (hubs.isLoading) {
    return <LoadingState />;
  }

  if (hubs.isError || !hubs.data || hubs.data.length === 0) {
    return (
      <div className="rago-page mx-auto max-w-xl px-6 pb-24 text-center">
        <h1 className="rago-display mb-3 text-2xl">Discovery is dark right now.</h1>
        <p className="text-sm leading-relaxed text-[var(--rago-text-2)]">
          We couldn't reach Rago Discovery. Give it a moment and try again.
        </p>
      </div>
    );
  }

  const hero = hubs.data[0]?.items[0];
  // The detail page's line — year · runtime or seasons · genres. The API
  // looks the lead title up, so it alone of the list carries the genres and
  // a film's runtime; whatever is missing drops out of the line.
  const heroFacts = hero ? metaFacts(hero) : null;
  const heroMeta = heroFacts
    ? [...heroFacts.parts, heroFacts.genres.slice(0, 3).join(", ")].filter(Boolean)
    : [];

  return (
    <div className="pb-16">
      {hero ? (
        <HeroBanner
          eyebrow="Rago Discovery"
          title={hero.title}
          backdropUrl={discoverySource.imageUrl(hero, "backdrop", 1920) || undefined}
          remember={{ sourceId: discoverySource.id, item: hero }}
          meta={heroMeta.length > 0 ? <MetaLine parts={heroMeta} /> : undefined}
          actions={
            <Link {...itemLink(hero, discoverySource)} className="rago-btn">
              View details
            </Link>
          }
        />
      ) : null}

      {/* relative: paints above the hero's fixed backdrop layer; the rows
          rise in after the hero's actions (Home's treatment). */}
      <div className={`${hero ? "relative mt-10" : "rago-page"} rago-rise rago-rise-4`}>
        {hubs.data.map((hub) => (
          <HubRow key={hub.id} hub={hub} source={discoverySource} caption="From Discovery" />
        ))}

        <p className="mt-4 px-6 text-xs text-[var(--rago-text-3)] sm:px-10">
          Browsing metadata from The Movie Database (TMDB). Titles here aren't in
          your libraries unless marked.
        </p>
      </div>
    </div>
  );
}
